import {
  CardContainer,
  P2GAlert,
  P2GBox,
  P2GCdnImage,
  P2GContainer,
  P2GGrid,
  P2GText,
  useP2GMediaQueryDown
} from '@parcel2go/core-ui';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import TrackingResultError from '~/components/tracking/error';
import { NoTrackingResultAdditionalContent } from '~/components/tracking/no-tracking-additional-content';
import NoTracking from '~/components/tracking/unknown';
import { hasDeliveryIssue } from '~/components/tracking/utils/mapParcelStage';
import useTracking from '~/hooks/use-tracking';
import { CreateCaseNoteResponse, TrackingEvent } from '~/lib/types/tracking-types';
import { Trans, useTranslation } from 'react-i18next';
import OrderHeader from '~/components/tracking/orderheader';
import TrackingNotifications from '~/components/tracking/trackingnotifications';
import Order from '~/components/tracking/order';
import { StagesOverview } from '~/components/tracking/overview';
import { InformationCards } from '~/components/tracking/informationcards';
import DeliveryShop from '~/components/tracking/shop';
import Recaptcha from '~/components/tracking/recaptcha';
import { CmsContent, useTenant } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { TranslationNamespaces } from '~/lib/constants';
import TrackingHistory from '~/components/tracking/tracking-history';

const TrackingResultLoading = () => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  return (
    <P2GGrid container direction="column" justify="space-around" alignItems="center" spacing={5}>
      <P2GGrid item></P2GGrid>
      <P2GGrid item>
        <P2GCdnImage
          alt=""
          src="/trackingpages/stages/tracking-loading.png"
          classNames="tracking-loading-image"
        ></P2GCdnImage>
      </P2GGrid>
      <P2GGrid item xs={6}>
        <P2GText classNames="p2g-tracking-loading-message" variant="h1">
          <Trans t={t} i18nKey="RESULT_LOADING_MESSAGE" defaults="One Moment While We Get Your Tracking Information" />
        </P2GText>
      </P2GGrid>
    </P2GGrid>
  );
};

export function Component() {
  const { isAuthenticated, signinRedirect, user } = useAuth();
  const [postcode, setPostcode] = useState<string>('');
  const { order } = useParams();
  const { tracking, isLoading, error } = useTracking({
    trackingReference: order,
    postcode: postcode,
    mostRecent: true
  });

  const [openValidation, setOpenValidation] = useState(false);
  const isBreak = useP2GMediaQueryDown('xs');
  const isSmallBreak = useP2GMediaQueryDown('sm');
  const profile = useTenant();

  const parcelReferences = useMemo(() => {
    if (!tracking) {
      return [];
    }
    return Object.entries(tracking.parcels).flatMap(([, value]) => {
      return value.parcel.parcelReferences;
    });
  }, [tracking]);

  const parcelNumbers = useMemo(() => {
    if (tracking) {
      const p = Object.getOwnPropertyNames(tracking.parcels);
      const stages = ['DroppedOff', 'Collected', 'InTransit', 'AtDepot', 'DeliveryScheduled', 'Delivered'];
      const s = p.sort((a, b) => {
        const parcelA = tracking.parcels[a];
        const parcelB = tracking.parcels[b];
        const aIssue = hasDeliveryIssue(parcelA) ? -99 : 0;
        const bIssue = hasDeliveryIssue(parcelB) ? -99 : 0;
        const aStage = stages.indexOf(parcelA.currentStage) + aIssue;
        const bStage = stages.indexOf(parcelB.currentStage) + bIssue;
        console.log(aStage, bStage);
        return aStage > bStage ? 1 : aStage < bStage ? -1 : 0;
      });
      return s;
    }
    return [];
  }, [tracking]);

  const hasAnyTracking = useMemo(() => {
    if (tracking) {
      if (parcelNumbers.length > 0) {
        for (const p in tracking.parcels)
          if (tracking.parcels[p].events.length > 1) {
            return true;
          }
      }
    }
    return false;
  }, [tracking, parcelNumbers.length]);

  const withWindow = useMemo(() => {
    let event: TrackingEvent | null = null;
    if (tracking) {
      for (const p of Object.getOwnPropertyNames(tracking.parcels)) {
        for (const e of tracking.parcels[p].events) {
          if (e.windowStart && e.windowEnd) {
            try {
              const endDate = new Date(e.windowEnd);
              if (endDate > new Date() && (!event || !event.windowEnd || event.windowEnd < e.windowEnd)) {
                event = e;
              }
            } catch {
              /* empty */
            }
          }
        }
      }
    }
    return event;
  }, [tracking]);

  const onSendCaseReply = useCallback(
    async (caseId: string, note: string) => {
      const token = user?.access_token;
      const response = await fetch(`${process.env.REACT_APP_API}/${profile.tenant}/cases/${caseId}/communication`, {
        method: 'POST',
        body: JSON.stringify(note),
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = (await response.json()) as CreateCaseNoteResponse;
      return data;
    },
    [user, profile]
  );

  if (isLoading) {
    return (
      <P2GContainer>
        <P2GBox pt={4} pb={4}>
          <TrackingResultLoading />
        </P2GBox>
      </P2GContainer>
    );
  }

  if (error && (error.code === '400.1' || error.code === '400.2' || error.code === '404.1' || error.code === '404.2')) {
    return (
      <P2GContainer>
        <P2GBox pt={4} pb={4}>
          <NoTracking />
        </P2GBox>
        <NoTrackingResultAdditionalContent />
      </P2GContainer>
    );
  }

  if (error || !tracking) {
    return (
      <P2GContainer>
        <P2GBox pt={4} pb={4}>
          <TrackingResultError />
        </P2GBox>
        <NoTrackingResultAdditionalContent />
      </P2GContainer>
    );
  }

  return (
    <Suspense fallback={<TrackingResultLoading />}>
      <P2GContainer>
        <P2GGrid container direction="column" spacing={isBreak ? 1 : 2}>
          <CmsContent
            contentKey="service-updates"
            wrapper={(e) => (
              <P2GGrid item>
                <P2GAlert>{e}</P2GAlert>
              </P2GGrid>
            )}
          />
          <CmsContent contentKey="tracking-result-header" wrapper={(e) => <P2GGrid item>{e}</P2GGrid>} />

          {tracking.order.foundOrder && (
            <P2GGrid item xs={12}>
              <OrderHeader
                tracking={tracking}
                onValidate={setPostcode}
                authenticated={isAuthenticated}
                signIn={signinRedirect}
                openValidation={openValidation}
                onCloseValidation={() => setOpenValidation(false)}
                onOpenValidation={() => setOpenValidation(true)}
              />
            </P2GGrid>
          )}

          <P2GGrid item>
            <CardContainer>
              <P2GGrid container direction="column" spacing={2} justify="space-around">
                <P2GGrid item>
                  <P2GGrid
                    container
                    alignItems="flex-start"
                    direction="row"
                    justify="space-between"
                    alignContent="center"
                    spacing={2}
                  >
                    <P2GGrid item xs={12} sm={8} md={9}>
                      <TrackingNotifications
                        tracking={tracking}
                        onOpenValidation={() => setOpenValidation(true)}
                        onSendReply={onSendCaseReply}
                        withWindow={withWindow}
                        parcelNumbers={parcelNumbers}
                        isPerParcelNotification={false}
                      />
                      {tracking.order.foundOrder && (
                        <Order
                          tracking={tracking}
                          onValidate={setPostcode}
                          authenticated={isAuthenticated}
                          signIn={signinRedirect}
                          openValidation={openValidation}
                          onCloseValidation={() => setOpenValidation(false)}
                          onOpenValidation={() => setOpenValidation(true)}
                          parcelNumber={parcelNumbers[0]}
                          parcel={tracking.parcels[parcelNumbers[0]]}
                          metadata={tracking.metadata}
                        />
                      )}
                    </P2GGrid>
                    <P2GGrid item xs={12} sm={4} md={3}>
                      {!isSmallBreak ? (
                        <P2GBox>
                          {parcelNumbers.length > 0 && tracking.started && (
                            <StagesOverview
                              parcels={parcelNumbers.length}
                              service={tracking.order.service}
                              overview={tracking.overview}
                            />
                          )}
                        </P2GBox>
                      ) : (
                        <>
                          {parcelNumbers.length > 0 && tracking.started && (
                            <StagesOverview
                              parcels={parcelNumbers.length}
                              service={tracking.order.service}
                              overview={tracking.overview}
                            />
                          )}
                        </>
                      )}
                    </P2GGrid>
                  </P2GGrid>
                </P2GGrid>
              </P2GGrid>
            </CardContainer>
          </P2GGrid>

          {tracking.order.foundOrder && (
            <InformationCards
              showFeedback={isAuthenticated && profile?.settings?.showTrackingFeedback === 'true'}
              keyEvents={tracking.parcels[parcelNumbers[0]].keyEvents}
              metadata={tracking.metadata}
              trackingNumber={parcelNumbers[0]}
              parcelReferences={parcelReferences}
              deliveryPostcode={tracking.order.deliveryPostcode}
              showLocalTracking={tracking.order.collectionCountryIso2Code !== tracking.order.deliveryCountryIso2Code}
            />
          )}

          {tracking.order.foundOrder &&
            tracking.validatedOwnership &&
            tracking.hasAwaitingShopPickupParcel &&
            tracking.deliveryShop && (
              <P2GGrid item>
                <DeliveryShop shop={tracking.deliveryShop} />
              </P2GGrid>
            )}

          {hasAnyTracking && (
            <P2GGrid item>
              <TrackingHistory
                tracking={tracking}
                parcelNumbers={parcelNumbers}
                withWindow={withWindow}
                setOpenValidation={setOpenValidation}
                onSendCaseReply={onSendCaseReply}
              />
            </P2GGrid>
          )}
          <P2GGrid item>
            <Recaptcha />
          </P2GGrid>
        </P2GGrid>
      </P2GContainer>
      <CmsContent contentKey={'LAYOUT.PREFOOTER'} />
    </Suspense>
  );
}

Component.displayName = 'Tracking';
